import React, { useEffect, useMemo, StrictMode, useState } from 'react'
import { SessionProvider } from 'next-auth/react'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { useFeatureFlags, withFeatureFlagProvider } from '@pff-consumer/feature-flags'
import { generateWebUiGlobalStyles } from '@pff-consumer/tweed-ui/web-styles/globals'
import { createUseStyles } from '@pff-consumer/tweed-ui/web-styles'
import { WebUiProvider } from '@pff-consumer/tweed-ui/web-styles/web-ui-provider'
import { nextJsDataProvider } from '@pff-consumer/data-provider/web'
import { ConnectAppContext } from '@pff-consumer/utils/lib/connect/connect-app-context'
import { CONNECT_PATH, CONNECT_TAB } from '@pff-consumer/utils/lib/connect/path-names'
import { useAnalytics } from '@pff-consumer/analytics'
import { ThemeContext } from '@pff-consumer/utils'
import { NextComponentType } from 'next'
import { Layout } from '../components/global/layout/layout'
import './_app.css'

// FIXME need reanimated update, see https://github.com/software-mansion/react-native-reanimated/issues/3355
if (process.browser) {
  // @ts-ignore
  // eslint-disable-next-line no-underscore-dangle
  window._frameTimestamp = null
}

type NextComponentWithAuthType = AppProps & {
  Component: NextComponentType & { auth?: boolean } // add auth type
}

const useGlobalStyles = createUseStyles({
  ...generateWebUiGlobalStyles({ fontUrlPath: '/assets/fonts' }),
})
const GlobalStyles = () => {
  useGlobalStyles()

  return null
}

/**
 * Call generateWebFontFaces() inside of a @global JSS property
 */

const App = ({ Component, pageProps: { session, ...pageProps } }: NextComponentWithAuthType) => {
  const { initializeAnalytics } = useAnalytics()
  const { getFeatureFlag } = useFeatureFlags()
  const isGameTabEnabled = getFeatureFlag('connect-show-game-tab', false)
  const isPlayerTabEnabled = getFeatureFlag('connect-show-player-tab', true)
  const isTeamTabEnabled = getFeatureFlag('connect-show-team-tab', false)
  const isUsageTabEnabled = getFeatureFlag('connect-show-live-tab', false)
  const isTeamInsightsTabEnabled = getFeatureFlag('connect-show-team-insights-tab', false)
  const [isPremiumSubscriber, setIsPremiumSubscriber] = useState<boolean>(false)

  useEffect(() => {
    const style = document.getElementById('server-side-styles')
    style?.parentNode?.removeChild(style)

    const urlParams = new URLSearchParams(window.location.search)
    const premiumParam = urlParams.get('isPremiumSubscriber')
    const isPremiumSubscriber = premiumParam === 'true'
    setIsPremiumSubscriber(isPremiumSubscriber)

    initializeAnalytics()
  }, [])

  const providerValue = useMemo(() => {
    return { isWeb: true }
  }, [])

  const defaultProviderValues = useMemo(() => {
    const tabs = [CONNECT_TAB.USAGE]
    const pathNames = [CONNECT_PATH.USAGE]

    if (isGameTabEnabled) {
      tabs.push(CONNECT_TAB.GAME)
      pathNames.push(CONNECT_PATH.GAME)
    }

    if (isPlayerTabEnabled) {
      tabs.push(CONNECT_TAB.PLAYER_INSIGHTS)
      pathNames.push(CONNECT_PATH.PLAYER_INSIGHTS)
    }

    if (isTeamTabEnabled) {
      tabs.push(CONNECT_TAB.TEAM)
      pathNames.push(CONNECT_PATH.TEAM)
    }

    if (isTeamInsightsTabEnabled) {
      tabs.push(CONNECT_TAB.TEAM_INSIGHTS)
      pathNames.push(CONNECT_PATH.TEAM_INSIGHTS)
    }

    return {
      isGameTabEnabled,
      isPlayerTabEnabled,
      isTeamTabEnabled,
      isUsageTabEnabled,
      isTeamInsightsTabEnabled,
      tabs,
      pathNames,
      isPremiumSubscriber,
    }
  }, [
    isGameTabEnabled,
    isPlayerTabEnabled,
    isTeamTabEnabled,
    isUsageTabEnabled,
    isTeamInsightsTabEnabled,
    isPremiumSubscriber,
  ])

  return (
    <StrictMode>
      <WebUiProvider isBrowserTheme>
        <GlobalStyles />
        <Head>
          <title>PFF Connect</title>
          <link
            rel='preconnect'
            crossOrigin='anonymous'
            href='https://fonts.googleapis.com'
          />
          <link
            rel='preconnect'
            crossOrigin='anonymous'
            href='https://fonts.gstatic.com'
          />
          <link
            href='https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap'
            crossOrigin='anonymous'
            rel='stylesheet'
          />
          <link
            href='https://fonts.googleapis.com/css2?family=Barlow:wght@400;700&display=swap'
            crossOrigin='anonymous'
            rel='stylesheet'
          />
        </Head>
        <SessionProvider session={session}>
          <ThemeContext.Provider value={providerValue}>
            <ConnectAppContext.Provider value={defaultProviderValues}>
              <Layout>
                <Component {...pageProps} />
              </Layout>
            </ConnectAppContext.Provider>
          </ThemeContext.Provider>
        </SessionProvider>
      </WebUiProvider>
    </StrictMode>
  )
}
// eslint-disable-next-line import/no-default-export
export default withFeatureFlagProvider()(nextJsDataProvider(App))
